import { useLayoutEffect } from 'react';
import { Platform } from 'react-native';

/**
 * While a modal dialog is open, hide the rest of the app from assistive tech (and `inert` where
 * supported) so VoiceOver doesn't read through to quantity steppers, cart copy, header dropdowns, etc.
 *
 * Prefers the topmost `[data-fhs-action-sheet-dialog]` node, else the topmost `[aria-modal="true"]`.
 * Walks from that node up toward `#root`. At each
 * level, every **sibling** of the current node gets `aria-hidden` + `inert`. The modal branch stays
 * exposed. Works when the modal lives under a single wrapper inside `#root` (typical RN Web / Expo).
 */
export function useSuppressWebRootAccessibility(isActive: boolean) {
  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || !isActive || typeof document === 'undefined') {
      return;
    }

    let cancelled = false;
    const undoFns: (() => void)[] = [];

    const hideElement = (el: HTMLElement) => {
      const prevHidden = el.getAttribute('aria-hidden');
      const hadInert = el.hasAttribute('inert');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('inert', '');
      undoFns.push(() => {
        if (prevHidden === null) {
          el.removeAttribute('aria-hidden');
        } else {
          el.setAttribute('aria-hidden', prevHidden);
        }
        if (!hadInert) {
          el.removeAttribute('inert');
        }
      });
    };

    const restoreAll = () => {
      while (undoFns.length > 0) {
        undoFns.pop()?.();
      }
    };

    const run = () => {
      if (cancelled) {
        return;
      }
      restoreAll();

      const root = document.getElementById('root');
      if (!(root instanceof HTMLElement)) {
        return;
      }

      const marked = Array.from(
        document.querySelectorAll('[data-fhs-action-sheet-dialog]')
      ).filter((n): n is HTMLElement => n instanceof HTMLElement);
      const ariaModals = Array.from(
        document.querySelectorAll('[aria-modal="true"]')
      ).filter((n): n is HTMLElement => n instanceof HTMLElement);
      const modal = marked[marked.length - 1] ?? ariaModals[ariaModals.length - 1];
      if (!modal) {
        return;
      }

      if (!root.contains(modal)) {
        hideElement(root);
        return;
      }

      let node: HTMLElement | null = modal;
      while (node && node !== root) {
        const parent = node.parentElement;
        if (!parent) {
          break;
        }
        for (const sib of parent.children) {
          if (sib !== node && sib instanceof HTMLElement) {
            hideElement(sib);
          }
        }
        node = parent;
      }
    };

    run();
    let retryTimeout = 0;
    const raf = requestAnimationFrame(() => {
      run();
      retryTimeout = window.setTimeout(run, 0);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(retryTimeout);
      restoreAll();
    };
  }, [isActive]);
}
